import {useState} from 'react';
import ExcelJS from 'exceljs/dist/es5/exceljs.browser';
import fileDownload from 'js-file-download';
import {toast} from 'react-toastify';
import {getReport, ReportFilterValues, ReportPayload} from '@app/services/reports';
import {humanise, monthYear} from '../components/labels';

/**
 * Excel export shared by every report.
 *
 * Follows the pattern already shipping in pages/printing-reports/
 * ExceededThreshold.tsx — exceljs + js-file-download, both already
 * dependencies. No second approach.
 *
 * ---------------------------------------------------------------------------
 * WHY THERE IS NO NATIVE EXCEL CHART
 *
 * Verified against the installed exceljs@4.4.0: there is no addChart or
 * addChartSheet API, and the library's own source says it lacks "the
 * infrastructure to support chartsheets". Its drawing module handles pictures
 * only. excel4node has no chart support either.
 *
 * So the workbook ships a "Chart Data" sheet — one clean rectangular range, no
 * merged cells, no blank rows, headers in row 1 — which a user selects and turns
 * into a real, live Excel chart with Alt+F1. That gives interactivity a pasted
 * image never could.
 * ---------------------------------------------------------------------------
 *
 * Volume: measured across all reports, only two exceed 50,000 raw rows, and both
 * collapse well below it once the detail sheet is the aggregate. Client-side is
 * sufficient; the 50k guard below is for the raw drill-down case.
 */

const ROW_CAP = 50000;
const HEADER_FILL = 'FF217346';

/**
 * Column formatting is decided on whole underscore-separated tokens, not on a
 * substring match.
 *
 * A substring regex looked fine and was wrong: /rate/ matches "auto_gene(rate)d",
 * so a boolean column was being formatted as £#,##0.00. Token matching makes the
 * intent explicit and cannot collide by accident.
 */
const CURRENCY_TOKENS = new Set([
  'price', 'value', 'amount', 'vat', 'net', 'gross', 'total',
  'outstanding', 'paid', 'fee', 'fees', 'rate', 'revenue', 'cost'
]);

const NEVER_CURRENCY_TOKENS = new Set([
  'count', 'qty', 'units', 'lines', 'orders', 'id', 'ids',
  'generated', 'status', 'type', 'name', 'share', 'pct', 'percent'
]);

const tokens = (key: string) => String(key).toLowerCase().split(/[_\s-]+/);

const isCurrencyKey = (key: string) => {
  const parts = tokens(key);
  if (parts.some((t) => NEVER_CURRENCY_TOKENS.has(t))) return false;
  return parts.some((t) => CURRENCY_TOKENS.has(t));
};

const isPercentKey = (key: string) =>
  tokens(key).some((t) => ['share', 'pct', 'percent'].includes(t));

/**
 * Category columns hold identifiers like other_item or handbook_combined. In the
 * workbook they read as labels, the same as on screen. A value with an
 * underscore is always an identifier; a single lower-case word (module, done) is
 * only converted in columns that are categories by name, so free text such as a
 * student's surname is never touched.
 */
const CATEGORY_TOKENS = new Set(['type', 'status', 'band', 'reliability']);

const exportValue = (key: string, value: any) => {
  if (typeof value !== 'string') return value;
  // "2026-08" -> "August 2026", matching the tables. Time-bucket values in other
  // forms (a day, a week) are returned unchanged by monthYear.
  if (key === 'period' || key === 'month' || key === 'bucket') return monthYear(value);
  if (value.includes('_') || tokens(key).some((t) => CATEGORY_TOKENS.has(t))) {
    return humanise(value);
  }
  return value;
};

const titleise = (key: string) =>
  key
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();

const styleHeader = (sheet: any, columnCount: number) => {
  const header = sheet.getRow(1);
  header.font = {bold: true, color: {argb: 'FFFFFFFF'}, size: 11};
  header.fill = {type: 'pattern', pattern: 'solid', fgColor: {argb: HEADER_FILL}};
  header.alignment = {vertical: 'middle'};
  header.height = 20;
  sheet.views = [{state: 'frozen', ySplit: 1}];
  if (columnCount > 0) {
    sheet.autoFilter = {
      from: {row: 1, column: 1},
      to: {row: 1, column: columnCount}
    };
  }
};

/**
 * Style by COLUMN, never by cell position — styling positionally is how a
 * currency format ends up on a "days overdue" column.
 */
const applyColumnFormats = (sheet: any, keys: string[], rows: any[]) => {
  // Width is sampled, not measured across every row: the detail sheet can carry
  // tens of thousands of rows, and spreading them into Math.max both allocates a
  // huge argument list and can blow the call-stack limit outright.
  const sample = rows.slice(0, 200);

  keys.forEach((key, index) => {
    const column = sheet.getColumn(index + 1);

    if (isCurrencyKey(key)) {
      column.numFmt = '£#,##0.00';
      column.alignment = {horizontal: 'right'};
    } else if (isPercentKey(key)) {
      column.numFmt = '0.0"%"';
      column.alignment = {horizontal: 'right'};
    } else if (tokens(key).some((t) => ['count', 'qty', 'units', 'lines', 'orders'].includes(t))) {
      column.alignment = {horizontal: 'right'};
    }

    const widest = sample.reduce(
      (max, row) => Math.max(max, String(row[key] ?? '').length + 2),
      titleise(key).length + 2
    );
    column.width = Math.min(Math.max(widest, 12), 42);
  });
};

/**
 * Optional per-report wording for the workbook, so it can say what the screen
 * says. `headers` renames a column heading by its API key (payment_source ->
 * "Type"); `values` rewrites individual values in a column by key
 * (order_status "sent" -> "Sent to Print Partner"). Both are optional and
 * leave every other column exactly as before.
 */
export interface ExportWording {
  headers?: Record<string, string>;
  values?: Record<string, Record<string, string>>;
}

const heading = (key: string, wording?: ExportWording) =>
  wording?.headers?.[key] || titleise(key);

const cellValue = (key: string, value: any, wording?: ExportWording) => {
  const mapped = wording?.values?.[key]?.[value];
  return mapped !== undefined ? mapped : exportValue(key, value);
};

const addTableSheet = (
  workbook: any,
  name: string,
  rows: any[],
  matched?: number,
  wording?: ExportWording
) => {
  const sheet = workbook.addWorksheet(name);
  if (!rows || !rows.length) {
    sheet.addRow(['No data for the selected filters']);
    return sheet;
  }
  const keys = Object.keys(rows[0]);
  const capped = rows.slice(0, ROW_CAP);
  sheet.addRow(keys.map((k) => heading(k, wording)));
  capped.forEach((row) => sheet.addRow(keys.map((k) => cellValue(k, row[k], wording))));
  styleHeader(sheet, keys.length);
  applyColumnFormats(sheet, keys, capped);
  const totalMatched = Math.max(matched ?? 0, rows.length);
  if (totalMatched > capped.length) {
    sheet.addRow([]);
    sheet.addRow([
      `Truncated: ${totalMatched.toLocaleString('en-GB')} rows matched, first ${capped.length.toLocaleString('en-GB')} exported.`
    ]);
  }
  return sheet;
};

/**
 * omitSeriesKeys drops columns from the Chart Data sheet only, for a report that
 * hides a series on screen the API still returns. Detail and Summary are left
 * alone so the workbook keeps agreeing with detail.total and the KPIs.
 *
 * headers / values apply the same on-screen wording to the Detail and Chart
 * Data sheets (see ExportWording).
 */
export const useReportExport = (
  reportName: string,
  options?: {omitSeriesKeys?: string[]} & ExportWording
) => {
  const [exporting, setExporting] = useState(false);

  /**
   * Re-requests the report with the same filters, page 1, and the `total` from
   * the previous response as the limit, so one call returns every row. Returns
   * the payload unchanged when the on-screen page already holds everything, or
   * when the caller did not pass a slug and filters.
   *
   * If the API returns fewer rows than asked for (it ignores a limit above its
   * own maximum), this throws rather than exporting a silently partial file.
   */
  const withAllDetailRows = async (
    payload: ReportPayload,
    slug?: string,
    filters?: ReportFilterValues
  ): Promise<ReportPayload> => {
    const detail = payload.collections.detail;
    if (!detail || !slug || !filters) return payload;
    if ((detail.rows || []).length >= detail.total) return payload;

    const target = Math.min(detail.total, ROW_CAP);
    const response = await getReport(slug, {...filters, page: 1, limit: target});
    if (!response || !response.success) {
      throw new Error('Report export request failed');
    }
    const rows: any[] = response.collections?.detail?.rows || [];
    if (rows.length < target) {
      throw new Error(`Expected ${target} rows, received ${rows.length}`);
    }

    return {
      ...payload,
      collections: {
        ...payload.collections,
        detail: {...detail, rows, page: 1, limit: target}
      }
    };
  };

  const exportToExcel = async (
    currentPayload: ReportPayload | null,
    slug?: string,
    filters?: ReportFilterValues
  ) => {
    if (!currentPayload || !currentPayload.collections) return;

    setExporting(true);
    try {
      let payload: ReportPayload;
      try {
        payload = await withAllDetailRows(currentPayload, slug, filters);
      } catch (error) {
        toast.error('Could not fetch all rows for the export. Please try again.');
        return;
      }

      const {filters_applied, kpis, series, detail} = payload.collections;
      const workbook = new ExcelJS.Workbook();
      workbook.created = new Date();

      // Sheet 1 — Filters. Without this an exported workbook is unauditable
      // once it has been emailed around.
      const filterSheet = workbook.addWorksheet('Filters');
      filterSheet.addRow(['Setting', 'Value']);
      filterSheet.addRow(['Report', reportName]);
      Object.entries(filters_applied || {}).forEach(([key, value]) => {
        filterSheet.addRow([
          titleise(key),
          Array.isArray(value)
            ? (value.length ? value.join(', ') : 'All')
            : value === null || value === undefined || value === ''
              ? 'All'
              : String(value)
        ]);
      });
      if (detail) filterSheet.addRow(['Rows Returned', detail.total]);
      styleHeader(filterSheet, 2);
      filterSheet.getColumn(1).width = 26;
      filterSheet.getColumn(2).width = 52;

      // Sheet 2 — Summary: the headline figures.
      //
      // The Value column holds mixed types — currency, counts and percentages —
      // so it cannot take a single column format. Each cell is formatted from
      // the KPI's own `format`, otherwise a 0.4% collection rate exports as the
      // bare number "0.4" and reads as either 0.4 or £0.40.
      const summary = workbook.addWorksheet('Summary');
      summary.addRow(['Measure', 'Value', 'Detail']);
      (kpis || []).forEach((k) => {
        const row = summary.addRow([k.label, k.value, k.detail || '']);
        const cell = row.getCell(2);
        if (k.format === 'currency') cell.numFmt = '£#,##0.00';
        else if (k.format === 'percent') cell.numFmt = '0.0"%"';
        else cell.numFmt = '#,##0';
        cell.alignment = {horizontal: 'right'};
      });
      styleHeader(summary, 3);
      summary.getColumn(1).width = 28;
      summary.getColumn(2).width = 18;
      summary.getColumn(2).alignment = {horizontal: 'right'};
      summary.getColumn(3).width = 42;

      // Sheet 3 — Detail: the full drill-down, unpaginated by intent.
      addTableSheet(workbook, 'Detail', detail ? detail.rows : [], detail?.total, options);

      // Sheet 4 — Chart Data: clean ranges for Alt+F1.
      const chartSheet = workbook.addWorksheet('Chart Data');
      let cursor = 1;
      Object.entries(series || {}).forEach(([name, rows]) => {
        if (!rows || !rows.length) return;
        const omit = options?.omitSeriesKeys || [];
        const keys = Object.keys(rows[0]).filter((k) => !omit.includes(k));
        chartSheet.getRow(cursor).values = [titleise(name)];
        chartSheet.getRow(cursor).font = {bold: true, size: 12};
        cursor += 1;
        chartSheet.getRow(cursor).values = keys.map((k) => heading(k, options));
        chartSheet.getRow(cursor).font = {bold: true};
        cursor += 1;
        rows.forEach((row: any) => {
          chartSheet.getRow(cursor).values = keys.map((k) => cellValue(k, row[k], options));
          cursor += 1;
        });
        cursor += 1; // one blank row between ranges keeps each selectable
      });
      chartSheet.getColumn(1).width = 26;
      [2, 3, 4, 5].forEach((i) => {
        chartSheet.getColumn(i).width = 16;
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const stamp = new Date().toISOString().slice(0, 10);
      const safeName = reportName.replace(/[^a-z0-9]+/gi, '_');
      fileDownload(
        new Blob([buffer]),
        `${safeName}_${filters_applied?.from || 'all'}_${filters_applied?.to || 'all'}_${stamp}.xlsx`
      );
    } finally {
      setExporting(false);
    }
  };

  return {exportToExcel, exporting};
};

export default useReportExport;
